import { CircularProgress, Button as MuiButton, Tooltip } from '@mui/material'
import type { ButtonProps as MuiButtonProps } from '@mui/material/Button'
import { alpha, useTheme } from '@mui/material/styles'
import type { ReactNode } from 'react'

export type ButtonVariant = 'text' | 'outlined' | 'contained'
export type ButtonSize = 'small' | 'medium' | 'large'
export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'error'
  | 'success'
  | 'warning'
  | 'info'
  | 'default'

interface ButtonProps extends Omit<MuiButtonProps, 'color'> {
  label: string | ReactNode
  variant?: ButtonVariant
  size?: ButtonSize
  color?: ButtonColor
  hoverColor?: ButtonColor
  disabled?: boolean
  textTransform?: 'none' | 'capitalize' | 'uppercase' | 'lowercase'
  loading?: boolean
  tooltip?: string
}

export default function Button({
  label,
  size = 'medium',
  variant = 'contained',
  onClick,
  color = 'primary',
  hoverColor,
  sx,
  disabled = false,
  textTransform = 'none',
  loading = false,
  tooltip,
  ...rest
}: ButtonProps) {
  const theme = useTheme()

  const getButtonColor = (colorType: ButtonColor) => {
    if (colorType === 'default') return theme.palette.text.primary
    return theme?.palette?.[colorType]?.main
  }

  const baseColor = getButtonColor(color)
  const hoverColorValue = hoverColor
    ? getButtonColor(hoverColor)
    : alpha(baseColor, 0.8)

  const getFontSize = (buttonSize: ButtonSize) => {
    switch (buttonSize) {
      case 'small':
        return '12px'
      case 'large':
        return '16px'
      default:
        return '14px'
    }
  }

  const getButtonStyles = (buttonSize: ButtonSize) => {
    switch (buttonSize) {
      case 'small':
        return {
          height: '30px',
          padding: '4px 10px',
        }
      case 'large':
        return {
          height: '42px',
          padding: '8px 22px',
        }
      default:
        return {
          height: '36px',
          padding: '8px 16px',
        }
    }
  }

  const button = (
    <MuiButton
      variant={variant}
      size={size}
      onClick={onClick}
      sx={{
        boxShadow:
          '0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)',
        borderRadius: '4px',
        color:
          variant === 'contained' ? theme.palette.background.paper : baseColor,
        backgroundColor: variant === 'contained' ? baseColor : 'transparent',
        textTransform,
        fontSize: getFontSize(size),
        borderColor: variant === 'outlined' ? baseColor : 'transparent',
        ...getButtonStyles(size),
        '&:hover': {
          backgroundColor:
            variant === 'contained' ? hoverColorValue : alpha(baseColor, 0.08),
          borderColor: variant === 'outlined' ? hoverColorValue : 'transparent',
        },
        '&.Mui-disabled': {
          backgroundColor: theme.palette.action.disabledBackground,
          color: theme.palette.action.text,
        },
        ...sx,
      }}
      disabled={disabled}
      {...rest}
    >
      {loading ? (
        <CircularProgress size={20} thickness={5} color="inherit" />
      ) : (
        label
      )}
    </MuiButton>
  )

  return tooltip ? (
    <Tooltip title={tooltip} placement="right" arrow>
      {button}
    </Tooltip>
  ) : (
    button
  )
}
